import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import * as XLSX from "xlsx";
import { FaAngleLeft } from "react-icons/fa";
import Loader from "../../../components/Loader/Loader";
import "../../../components/common/css/campaignview.scss";
import "../../new/new.scss";
import { DataGrid } from "@mui/x-data-grid";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import { useCountries, bankCountryLabel } from "../../../utils/bankDisplay";
// I9 (corporate Business PAD contract): account numbers and IBANs are shown
// as the last 4 only.
import { maskAccount } from "../../../utils/bankMask";
import { resolvedOrderKind, accountKindReasonText } from "../../../utils/orderAccountKind";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";
import {
  apiErrorMessage,
  isSubmissionSuperseded,
  submissionSupersededMessage,
} from "../../../utils/apiError";
// The revision of the submission the answers on screen belong to (API round 14):
// result.submissionRev of the answers read; null when it named none.
import { submissionRevOf } from "../../../utils/suitabilityRevision";

const MIN_NOTES = 10;
const OVERSIGHT_HINT = "Decided by a compliance reviewer";
const DECIDE_FORBIDDEN =
  "Only a Dealing Representative, CCO or UDP can decide suitability.";
const DETAILS_REFUSED =
  "Investment details are shown to the platform admin only. The answers and the decision below are available to compliance reviewers.";
const NO_SUBMISSION_REV =
  "This page could not tell which submission these answers belong to, so it cannot record a decision. Reload the page.";

const Viewinvestment = () => {
  const navigate = useNavigate();
  // Suitability is a compliance decision like any other. It used to be
  // recorded by emailing a chosen "approver", which named someone who had not
  // been asked; the decision now goes to the compliance service, which records
  // the actor itself.
  const { canDecide } = useRole();
  const id = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [decideMode, setDecideMode] = useState(null); // "APPROVED" | "REJECTED"
  const [decideNotes, setDecideNotes] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [suitabilityData, setSuitabilityData] = useState([]);
  const [suitabilityStatus, setSuitabilityStatus] = useState("");
  const [submissionRev, setSubmissionRev] = useState(null);
  const [detailsRefused, setDetailsRefused] = useState(false);
  const [suitabilityLoading, setSuitabilityLoading] = useState(false);
  const countries = useCountries();
  // I1 (corporate Business PAD contract): the bank block follows the order's
  // RESOLVED kind (the API's accountKind and accountKindReason only; never the
  // order's own investmentAccount, never the user's entity_type). An
  // unresolved order (one the server sent no kind for included) shows neither
  // kind's coordinates.
  const resolved = resolvedOrderKind(data);
  const kind = resolved.kind;
  const [pageState, setPageState] = useState({
    data: [],
    total: 0,
    page: 1,
    pageSize: 9,
  });
  const fetchjobData = () => {
    const token = localStorage.getItem("accessToken");
    setLoading(true);
    var config = {
      method: "post",
      url: process.env.REACT_APP_API + "admin/get-myinvestment-details-by-id",
      headers: {
        "x-access-token": token,
      },
      data: {
        id: id.suitabilityId,
      },
    };

    axios(config)
      .then(function (response) {
        setLoading(false);
        setDetailsRefused(false);
        setData(response.data.data.rows[0]);
      })
      .catch(function (error) {
        setLoading(false);
        // A compliance reviewer may decide here without the investment
        // details, which are platform-admin only: say so on the page.
        if (error?.response?.status === 403) {
          setDetailsRefused(true);
          return;
        }
        Swal.fire({
          title: error?.response?.data?.message || "An error occurred",
          timerProgressBar: true,
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  };

  const fetchSuitabilityData = () => {
    const token = localStorage.getItem("accessToken");
    setSuitabilityLoading(true);
    // No decision against answers being replaced on screen.
    setSubmissionRev(null);

    // Get user ID from the data if available

    var config = {
      method: "get",
      url:
        process.env.REACT_APP_API +
        `investorProfile/suitability-assestment-result?investment_id=${
          id?.suitabilityId
        }&forAdmin=${true}`,
      headers: {
        "x-access-token": token,
      },
    };

    axios(config)
      .then(function (response) {
        setSuitabilityLoading(false);
        setSuitabilityData(response?.data?.data || []);
        setSuitabilityStatus(response?.data?.result || "");
        setSubmissionRev(submissionRevOf(response?.data?.result));
      })
      .catch(function (error) {
        setSuitabilityLoading(false);
        console.error("Error fetching suitability data:", error);
        Swal.fire({
          title: "Error loading suitability test data",
          text: error.response?.data?.message || "Something went wrong",
          timerProgressBar: true,
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  };

  // Both reads start at once. The answers are read by the route's investment
  // id and do not wait for the investment details (Codex r13 P2): the details
  // read is platform-admin only, while the answers read also admits the
  // compliance reviewers who decide here (API round 14b).
  useEffect(() => {
    fetchjobData();
    fetchSuitabilityData();
  }, []);

  function openDecision(decision) {
    setDecideMode(decision);
    setDecideNotes("");
  }

  async function submitDecision() {
    const notes = decideNotes.trim();
    if (notes.length < MIN_NOTES || submissionRev === null) return;
    setDeciding(true);
    try {
      await drApi.decideInvestmentSuitability(id.suitabilityId, {
        decision: decideMode,
        notes,
        submission_rev: submissionRev,
      });
      setDecideMode(null);
      Swal.fire({
        title: decideMode === "APPROVED" ? "Suitability approved" : "Suitability rejected",
        icon: "success",
        confirmButtonText: "OK",
      });
      navigate("/suitability-overview");
    } catch (error) {
      Swal.fire({
        title: "Error",
        text:
          error?.response?.status === 403
            ? DECIDE_FORBIDDEN
            : isSubmissionSuperseded(error)
              ? submissionSupersededMessage(error)
              : apiErrorMessage(error, "Something went wrong"),
        icon: "error",
        confirmButtonText: "OK",
      });
      // The answers changed after this page loaded: deciding again from it
      // would be refused again, so it shows the current submission instead.
      if (isSubmissionSuperseded(error)) {
        setDecideMode(null);
        fetchSuitabilityData();
      }
    } finally {
      setDeciding(false);
    }
  }

  const decisionButtonStyle = (color, enabled) => ({
    padding: "10px 20px",
    backgroundColor: enabled ? color : "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "bold",
    cursor: enabled ? "pointer" : "not-allowed",
  });

  // Reusable style function
  const getStatusBoxStyle = (type) => {
    const baseStyle = {
      padding: "15px",
      borderRadius: "4px",
      marginBottom: "20px",
      fontSize: "14px",
      fontWeight: "500",
      width: "50%",
      maxWidth: "700px",
    };

    if (type === "accept") {
      return {
        ...baseStyle,
        backgroundColor: "#d4edda",
        border: "1px solid #c3e6cb",
        color: "#155724",
      };
    }

    if (type === "reject") {
      return {
        ...baseStyle,
        backgroundColor: "#f8d7da",
        border: "1px solid #f5c6cb",
        color: "#721c24",
      };
    }

    if (type === "pending") {
      return {
        ...baseStyle,
        backgroundColor: "#fff3cd",
        border: "1px solid #ffeeba",
        color: "#856404",
      };
    }

    return baseStyle;
  };

  const fetchStabilityHistoryData = () => {
    const token = localStorage.getItem("accessToken");
    setLoading(true);
    setPageState((old) => ({ ...old }));
    var config = {
      method: "post",
      url: process.env.REACT_APP_API + "admin/history-suitability",
      headers: {
        "x-access-token": token,
      },
      data: {
        page: pageState.page,
        limit: pageState.pageSize,
        investmentId: id.suitabilityId,
      },
    };

    axios(config)
      .then(function (response) {
        setPageState((old) => ({
          ...old,
          data: response?.data?.data,
          total: response?.data?.data?.length,
        }));
        setLoading(false);
      })
      .catch(function (error) {
        setLoading(false);
        Swal.fire({
          title: error?.response?.data?.message || "An error occurred",
          timerProgressBar: true,
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  };

  useEffect(() => {
    fetchStabilityHistoryData();
  }, [pageState.page, pageState.pageSize]);

  const userColumns = [
    {
      field: "investment_id",
      headerName: "Investment Id",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => {
        return (
          <div className="rowitem">{params?.row?.investment_id || "N/A"}</div>
        );
      },
    },

    {
      field: "performed_by",
      headerName: "Performed By",
      flex: 1,
      minWidth: 250,
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => {
        const d = new Date(params?.row?.updatedAt);
        return (
          <div className="cellWrapper ">
            <div className="profilePic">
              {d?.getHours() + ":" + d?.getMinutes() + ", " + d?.toDateString()}
            </div>
          </div>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 250,
    },
  ];
  return (
    <div className="new">
      <Loader isLoading={loading} />
      <div className="newContainer">
        <div className="top">
          <span className="backarrow">
            <FaAngleLeft onClick={() => navigate(-1)} />
          </span>
          <h1 className="view-page-tittle">View Suitability Test</h1>
        </div>
        {detailsRefused && (
          <p role="note" style={{ fontSize: 13, color: "#6b7280", margin: "8px 0" }}>
            {DETAILS_REFUSED}
          </p>
        )}
        <PageShell><PageHead title="Investment payment detail" /><div className="single">
          <div className="singleContainer">
            <div className="top">
              <div
                className="left"
                style={{ textAlign: "left", boxShadow: "unset" }}
              >
                <div className="topHeading">
                  {" "}
                  <h1
                    className="title"
                    style={{ fontSize: "16px", color: "#333", width: "100%" }}
                  >
                    Corporate Information
                  </h1>
                  <div className="exportBtn-divSec"></div>
                </div>

                <div className="item">
                  <div className="details profile-info">
                    <div className="detailItem">
                      <span className="itemKey">Company Name:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.corporation_legal_name}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Company Email</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.camp_email}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Company Address:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.registered_address}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">City</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.city}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Country</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.country_name || "-"}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">State/Province</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.state_name || "-"}
                      </span>
                    </div>
                    {data?.campaign_detail?.type === "FUND" && (
                      <div className="detailItem">
                        <span className="itemKey">Issuer jurisdiction</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.issuer_jurisdiction || "-"}
                        </span>
                      </div>
                    )}

                    <div className="detailItem">
                      <span className="itemKey">Type of entity:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.entitytype}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Business number:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.business}
                      </span>
                    </div>

                    <div className="detailItem">
                      <span className="itemKey">Fax</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.fax !== "undefined" &&
                        data?.campaign_detail?.fax !== "null"
                          ? data?.campaign_detail?.fax
                          : "-"}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">CRA tax account</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.craax_account !== "undefined" &&
                        data?.campaign_detail?.craax_account !== "null"
                          ? data?.campaign_detail?.craax_account
                          : "-"}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Date Of Corp</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.date_of_incorp}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Year Of Corporation</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.how_many_yearsin_business}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div></PageShell>
        <div className="single">
          <div className="singleContainer">
            <div className="top">
              <div
                className="left"
                style={{ textAlign: "left", boxShadow: "unset" }}
              >
                <div className="topHeading">
                  {" "}
                  <h1
                    className="title"
                    style={{ fontSize: "16px", color: "#333", width: "100%" }}
                  >
                    Personal Information
                  </h1>
                </div>

                {kind === "INDIVIDUAL" ? (
                  <div className="item">
                    <div className="details profile-info">
                      <div className="detailItem">
                        <span className="itemKey">First Name</span>
                        <span className="itemValue">
                          {data?.user?.firstname}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Last Name</span>
                        <span className="itemValue">
                          {data?.user?.lastname}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Investment Account</span>
                        <span className="itemValue">
                          {data?.investmentAccount}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign ID</span>
                        <span className="itemValue">{data?.campaign_id}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign Name</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.campaign_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Corporate Name</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.corporation_legal_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Amount</span>
                        <span className="itemValue">{data?.amount}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Currency</span>
                        <span className="itemValue">{data?.currency}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Bank Country</span>
                        <span className="itemValue">
                          {bankCountryLabel(
                            countries,
                            data?.user?.user_banking_details?.bank_country_id
                          )}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Bank Name</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.bank_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">IBAN</span>
                        <span className="itemValue">
                          {maskAccount(data?.user?.user_banking_details?.iban)}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">SWIFT Code</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.swift_code}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Institutional Number</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.institution_number}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Transit / Routing Number</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.transit_number}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Account Number</span>
                        <span className="itemValue">
                          {maskAccount(data?.user?.user_banking_details?.account_number)}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Account Holder</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.account_holder}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Order Number</span>
                        <span className="itemValue">{data?.order_id}</span>
                      </div>

                      <div className="detailItem">
                        <span className="itemKey">Payment Method</span>
                        <span className="itemValue">{data?.paymentOption}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Payment Status</span>
                        <span className="itemValue">{data?.paymentStatus}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="item">
                    <div className="details profile-info">
                      <div className="detailItem">
                        <span className="itemKey">Corporation Name</span>
                        <span className="itemValue">
                          {data?.user?.comp__name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Investment Account</span>
                        <span className="itemValue">
                          {data?.investmentAccount}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign ID</span>
                        <span className="itemValue">{data?.campaign_id}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign Name</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.corporation_legal_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Amount</span>
                        <span className="itemValue">{data?.amount}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Currency</span>
                        <span className="itemValue">{data?.currency}</span>
                      </div>
                      {/* I1: the company's coordinates only for a resolved
                          ENTITY order; an unresolved one shows none. */}
                      {kind === "ENTITY" ? (
                        <>
                          <div className="detailItem">
                            <span className="itemKey">Bank Country</span>
                            <span className="itemValue">
                              {bankCountryLabel(
                                countries,
                                data?.user?.user_banking_details
                                  ?.corporate_bank_country_id
                              )}
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Bank Name</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_bank_name
                              }
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">IBAN</span>
                            <span className="itemValue">
                              {maskAccount(data?.user?.user_banking_details?.corporate_iban)}
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">SWIFT Code</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_swift_code
                              }
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Institutional Number</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_institution_number
                              }
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Transit / Routing Number</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_transit_number
                              }
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Account Number</span>
                            <span className="itemValue">
                              {maskAccount(
                                data?.user?.user_banking_details
                                  ?.corporate_account_number
                              )}
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Account Holder</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_account_holder
                              }
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="detailItem">
                          <span className="itemKey">Bank details</span>
                          <span className="itemValue">
                            Not shown: this order's account kind is not
                            resolved ({accountKindReasonText(resolved.reason)}).
                          </span>
                        </div>
                      )}

                      <div className="detailItem">
                        <span className="itemKey">Order Number</span>
                        <span className="itemValue">{data?.order_id}</span>
                      </div>

                      <div className="detailItem">
                        <span className="itemKey">Payment Method</span>
                        <span className="itemValue">{data?.paymentOption}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Payment Action</span>
                        <span className="itemValue">{data?.paymentStatus}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="single">
          <div className="singleContainer">
            <div className="top">
              <div
                className="left"
                style={{ textAlign: "left", boxShadow: "unset" }}
              >
                <div className="topHeading">
                  {" "}
                  <h1
                    className="title"
                    style={{ fontSize: "16px", color: "#333", width: "100%" }}
                  >
                    Suitability Test Information
                  </h1>
                  <div className="exportBtn-divSec"></div>
                </div>

                <div className="item">
                  <div className="details profile-info">
                    {suitabilityLoading ? (
                      <div style={{ textAlign: "center", padding: "20px" }}>
                        <p>Loading suitability test data...</p>
                      </div>
                    ) : suitabilityData && suitabilityData.length > 0 ? (
                      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        {suitabilityData.map((item, index) => (
                          <li
                            key={item.id}
                            style={{
                              marginBottom: "25px",
                              padding: "15px",
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <div style={{ marginBottom: "10px" }}>
                              <strong
                                style={{ color: "#333", fontSize: "14px" }}
                              >
                                {index + 1}.{" "}
                                {item.suitability_assestment_question
                                  ?.question || "N/A"}
                              </strong>
                            </div>
                            <div style={{ color: "#555", fontSize: "13px" }}>
                              <strong>Answer:</strong>{" "}
                              {item.suitability_assestment_option?.option_en ||
                                item.user_answer ||
                                "N/A"}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div style={{ textAlign: "center", padding: "20px" }}>
                        <p>No suitability test data available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Approval Section */}

                {/* Show message if approverName exists */}
                {data?.is_suitability_accepted == 0 &&
                data?.suitabilityApproveInitiated == 1 ? (
                  <div style={getStatusBoxStyle("accept")}>
                    {`${suitabilityStatus?.approverName} has initiated the acceptance request and it is awaiting approval.`}
                  </div>
                ) : data?.is_suitability_accepted == 1 &&
                  suitabilityStatus?.approverName != null ? (
                  <div style={getStatusBoxStyle("accept")}>
                    {`The ${suitabilityStatus?.approverName} user has already approved this request..`}
                  </div>
                ) : data?.isSuitabilityRejected == 0 &&
                  data?.suitabilityRejectInitiated == 1 ? (
                  <div style={getStatusBoxStyle("reject")}>
                    {`${suitabilityStatus?.rejectorName} has initiated the rejection request and it is awaiting final rejection.`}
                  </div>
                ) : data?.isSuitabilityRejected == 1 &&
                  suitabilityStatus?.rejectorName != null ? (
                  <div style={getStatusBoxStyle("reject")}>
                    {`${suitabilityStatus?.rejectorName} has already rejected this request.`}
                  </div>
                ) : (
                  (!suitabilityStatus?.approverName ||
                    suitabilityStatus?.approverName === null ||
                    suitabilityStatus?.approverName === "") &&
                  (!suitabilityStatus?.rejectorName ||
                    suitabilityStatus?.rejectorName === null ||
                    suitabilityStatus?.rejectorName === "") && (
                    <div
                      style={{
                        marginTop: "30px",
                        padding: "20px",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <h3
                        style={{
                          marginBottom: "20px",
                          color: "#333",
                          fontSize: "16px",
                        }}
                      >
                        Approve Suitability Test
                      </h3>


                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                        <button
                          onClick={() => openDecision("APPROVED")}
                          disabled={!canDecide || deciding || submissionRev === null}
                          title={canDecide ? undefined : OVERSIGHT_HINT}
                          style={decisionButtonStyle("#28a745", canDecide && !deciding && submissionRev !== null)}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openDecision("REJECTED")}
                          disabled={!canDecide || deciding || submissionRev === null}
                          title={canDecide ? undefined : OVERSIGHT_HINT}
                          style={decisionButtonStyle("#dc3545", canDecide && !deciding && submissionRev !== null)}
                        >
                          Reject
                        </button>
                        {!canDecide && (
                          <span style={{ fontSize: 13, color: "#6b7280" }}>
                            {OVERSIGHT_HINT}
                          </span>
                        )}
                        {canDecide && submissionRev === null && !suitabilityLoading && (
                          <span role="note" style={{ fontSize: 13, color: "#6b7280" }}>
                            {NO_SUBMISSION_REV}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                )}

                {decideMode && (
                  <div
                    role="dialog"
                    aria-labelledby="suitability-decision-title"
                    aria-modal="true"
                    style={{
                      marginTop: "20px",
                      padding: "20px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      background: "#fff",
                    }}
                  >
                    <h3 id="suitability-decision-title" style={{ marginTop: 0, fontSize: "16px" }}>
                      {decideMode === "APPROVED"
                        ? "Approve this suitability assessment"
                        : "Reject this suitability assessment"}
                    </h3>
                    <label
                      htmlFor="suitability-decision-notes"
                      style={{ display: "block", fontWeight: "bold", marginBottom: 4 }}
                    >
                      Decision notes (mandatory, min {MIN_NOTES} chars)
                    </label>
                    <textarea
                      id="suitability-decision-notes"
                      rows={4}
                      value={decideNotes}
                      disabled={deciding}
                      onChange={(e) => setDecideNotes(e.target.value)}
                      style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                    />
                    <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                      <button
                        onClick={() => setDecideMode(null)}
                        disabled={deciding}
                        style={decisionButtonStyle("#6c757d", !deciding)}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={submitDecision}
                        disabled={deciding || decideNotes.trim().length < MIN_NOTES}
                        style={decisionButtonStyle(
                          decideMode === "APPROVED" ? "#28a745" : "#dc3545",
                          !deciding && decideNotes.trim().length >= MIN_NOTES
                        )}
                      >
                        {deciding ? "Processing..." : "Record decision"}
                      </button>
                    </div>
                  </div>
                )}

                <DataGrid
                  className="datagrid"
                  autoHeight
                  rows={pageState.data}
                  rowCount={pageState.total}
                  rowsPerPageOptions={[10, 30, 50, 70, 100]}
                  pagination
                  page={pageState.page - 1}
                  pageSize={pageState.pageSize}
                  paginationMode="server"
                  onPageChange={(newPage) => {
                    setPageState((old) => ({ ...old, page: newPage + 1 }));
                  }}
                  onPageSizeChange={(newPageSize) =>
                    setPageState((old) => ({ ...old, pageSize: newPageSize }))
                  }
                  columns={userColumns}
                  localeText={{
                    noRowsLabel: "No data available",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Viewinvestment;
